export interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  company_name?: string;
  type?: string;
  rank?: string;
  relationship?: string;
  status?: string;
  remarket: string;
  last_contacted: string | null;
  last_contact_type?: string;
  /**
   * Retired (issue #772). Nothing writes this any more — the cadence feature moved to
   * next_contact, which the daily list gates on. Still selected by the contacts RPC
   * because the column exists; do not read it for behaviour and do not resurrect it as
   * a sort or filter field. 48 rows carry stale historical values.
   */
  skip_until: string | null;
  next_contact: string | null;
  /** Explicit per-contact cadence in days. NULL = follows the 30-day system default. */
  next_contact_interval_days?: number | null;
  next_activity_date?: string | null;
  created_at: string;
  workspace_id: string;
  title?: string;  // Salutation
  job_title?: string;
  assigned_to?: string;
  assigned_to_name?: string;
  // Source tracking fields (auto-set for API imports)
  source?: 'api' | 'manual' | 'csv_import';
  import_batch?: string;
  imported_at?: string;
  // Unsubscribe fields
  unsubscribed?: boolean;
  unsubscribed_at?: string | null;
  // Soft delete fields
  deleted_at?: string | null;
  deleted_by?: string | null;
}

export type ContactOptionCategory = 'relationship' | 'type' | 'rank' | 'status' | 'call_outcome';

export interface ContactFormValues {
  first_name: string;
  last_name: string;
  type?: string;
  rank?: string;
  relationship?: string;
  status?: string;
  is_company: boolean;
}

export interface SortField {
  field: string;
  direction: 'ASC' | 'DESC';
  // Where NULLs land in the sort. When omitted, the RPC defaults to 'last'
  // (back-compat with pre-rotation saved views). The Rotation preset sets
  // this explicitly to 'first' so unscheduled contacts surface at the top.
  nulls?: 'first' | 'last';
}

export const SORTABLE_FIELDS: { value: string; label: string }[] = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'company_name', label: 'Company' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'type', label: 'Type' },
  { value: 'rank', label: 'Rank' },
  { value: 'status', label: 'Status' },
  { value: 'relationship', label: 'Relationship' },
  { value: 'remarket', label: 'Remarket' },
  { value: 'created_at', label: 'Created Date' },
  { value: 'updated_at', label: 'Updated Date' },
  { value: 'last_contacted', label: 'Last Contacted' },
  { value: 'next_contact', label: 'Next Contact' },
  { value: 'primary_email', label: 'Email' },
  { value: 'primary_phone', label: 'Phone' },
  { value: 'last_contact_type', label: 'Last Contact Type' },
  // Rotation v2 virtual sort fields (computed in filter_contacts). Not real columns;
  // used by the Daily call filter. See docs/superpowers/specs/2026-07-11-call-filters-*.
  { value: 'call_priority', label: 'Call Priority (Daily rotation)' },
  { value: 'next_scheduled_event', label: 'Upcoming Event (≤3 days)' },
];

export interface CreateContactFormValues {
  first_name: string;
  last_name: string;
  company_name: string;
  is_company: boolean;
  phones: Array<{ label: string; phone_number: string }>;
  emails: Array<{ label: string; email: string }>;
  addresses: Array<{
    label: string;
    formatted: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    lat: string | null;
    long: string | null;
  }>;
  custom_fields: Record<string, string>;
  type: string;
  rank: string;
  relationship: string;
  status: string;
  // New fields for contacts table
  title: string;          // Salutation: Mr., Mrs., Dr., Ms., etc.
  job_title: string;      // Job title
  industry: string;       // Industry
  // New expandable section fields (saved to child tables after contact creation)
  note: string;                          // Saved to 'notes' table
  event_title: string;                   // Saved to activities
  event_date: string;                    // Saved to activities
  important_date: string;                // Saved to 'important_dates' table
  important_date_label: string;          // Label for the date
  websites: Array<{ url: string; label: string }>;  // Saved to 'website_domains' table
  group_name: string;                    // Saved to 'groups' + 'contact_group_members'
  connection_name: string;               // Deferred - for post-creation
  connection_contact_id: string;         // Deferred - selected contact ID
  connection_relationship: string;       // Deferred - for post-creation
}

export interface ContactFilters {
  group_type?: 'AND' | 'OR';
  search?: string;
  groups?: Array<{
    id: string;
    type: 'AND' | 'OR';
    conditions: Array<{
      id: string;
      table?: string;  // Table name (contacts, email_address, etc.)
      field: string;
      operator: string;
      value: string;
      valueEnd?: string;  // For 'between' operator
      // For important_dates.date: also require this label on the SAME row (correlated).
      label?: string;

      // Relative date fields
      dateMode?: 'absolute' | 'relative';
      relativeValue?: number;
      relativeUnit?: 'days';
      relativeDirection?: 'ago' | 'from_now';
      // Between operator: second relative date fields
      relativeValueEnd?: number;
      relativeDirectionEnd?: 'ago' | 'from_now';
    }>;
  }>;
  sort?: SortField[];
  contactIds?: string[];  // Filter to only these contact IDs (e.g., group members)
}